interface FailureCacheEntry {
  failedAt: number;
  ttlMs: number;
  status: number;
}

const DEFAULT_FAILURE_TTL_MS = 5 * 60 * 1000;
const RATE_LIMIT_FAILURE_TTL_MS = 15 * 60 * 1000;
const SERVICE_UNAVAILABLE_FAILURE_TTL_MS = 2 * 60 * 1000;

const failureCache = new Map<string, FailureCacheEntry>();

function ttlForStatus(status: number): number {
  if (status === 429) {
    return RATE_LIMIT_FAILURE_TTL_MS;
  }
  if (status === 503) {
    return SERVICE_UNAVAILABLE_FAILURE_TTL_MS;
  }
  return DEFAULT_FAILURE_TTL_MS;
}

export function buildRequestFailureCacheKey(parts: readonly string[]): string {
  return parts.join('|');
}

export function shouldSkipCachedFailure(key: string): boolean {
  const entry = failureCache.get(key);
  if (!entry) {
    return false;
  }
  if (Date.now() - entry.failedAt > entry.ttlMs) {
    failureCache.delete(key);
    return false;
  }
  return true;
}

export function markRequestFailure(key: string, status: number): void {
  failureCache.set(key, {
    failedAt: Date.now(),
    ttlMs: ttlForStatus(status),
    status,
  });
}

export function clearRequestFailure(key: string): void {
  failureCache.delete(key);
}

export function clearAllRequestFailures(): void {
  failureCache.clear();
}

export class CachedRequestFailureError extends Error {
  public readonly cacheKey: string;
  public readonly status: number;

  public constructor(cacheKey: string, status: number) {
    super(`Skipping cached failure for ${cacheKey}`);
    this.name = 'CachedRequestFailureError';
    this.cacheKey = cacheKey;
    this.status = status;
  }
}

export function getCachedFailureStatus(key: string): number | undefined {
  const entry = failureCache.get(key);
  if (!entry) {
    return undefined;
  }
  if (Date.now() - entry.failedAt > entry.ttlMs) {
    failureCache.delete(key);
    return undefined;
  }
  return entry.status;
}
